import React, { useEffect, useState } from 'react';
import { Button, message, Result, Spin } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import UserStore from '@/stores/UserStore';
import Verify from '@/components/Verify';
import { ErrorRender } from '@partsbadger/library';
import QuoteStore from '@/stores/QuoteStore';
import { AxiosError } from 'axios';

type RouteParams = { id: string };

const QuotePdfOrderRedirect: React.FC = () => {
    const { id: idParam } = useParams<RouteParams>();
    const history = useHistory();
    const quoteId = Number(idParam);
    const isValidId = Number.isInteger(quoteId) && quoteId > 0;
    const isVerified = UserStore.isVerified;

    const [loading, setLoading] = useState(() => isValidId);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        if (!isVerified || !isValidId) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        QuoteStore.createQuoteNewRevision(quoteId)
            .then(data => {
                if (cancelled) return;
                message.success(`Revision ${data.name} created`);
                history.replace(`/my-account/quotes/${data.id}/order/`);
            })
            .catch(err => {
                if (cancelled) return;
                message.error('Something went wrong!');
                setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [quoteId, isValidId, history, isVerified]);

    if (!isVerified) {
        return <Verify />;
    }

    if (!isValidId) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[40vh] px-4">
                <Result
                    status="404"
                    title="Invalid quote link"
                    subTitle="This order link is not valid. Check the link in your PDF or open the quote from your account."
                    extra={
                        <Button type="primary">
                            <Link to="/my-account/quotes">Back to quotes</Link>
                        </Button>
                    }
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex flex-row items-center justify-center min-h-[50vh]">
                <Spin size="large" tip="Preparing your order…" />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] px-4">
            <Result
                status="error"
                title="Could not start order"
                subTitle={<ErrorRender error={error as AxiosError<any>} />}
                extra={
                    <div className="flex flex-col items-center justify-center">
                        <Button type="primary" onClick={() => window.location.reload()}>
                            Try again
                        </Button>
                        <Link to={`/my-account/quotes/${quoteId}`} className="mt-2">
                            Back to quote
                        </Link>
                    </div>
                }
            />
        </div>
    );
};

export default observer(QuotePdfOrderRedirect);
